import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, UserPlus, Mail, Lock, User, Building, Hash } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { saveCustomer, customerLogin } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const CustomerLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Get the return URL from state or default to account page
  const returnUrl = location.state?.from?.pathname || '/account';

  const [loginData, setLoginData] = useState({
    userName: '',
    password: '',
  });

  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    userName: '',
    password: '',
    confirmPassword: '',
    roleId: 0,
    customerAccount: 0,
    nationalIdentity: '',
  });

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSignupChange = (e) => {
    setSignupData({
      ...signupData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.userName || !loginData.password) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await customerLogin({
        userName: loginData.userName,
        password: loginData.password,
      });

      // Use AuthContext to store user and token
      if (response?.token || response?.access_token) {
        const token = response.token || response.access_token;
        // User object now includes customer and customerAccount details from backend
        const user = response.user || {
          userName: loginData.userName,
          userId: response.user?.userId || response.user?.id,
          role: response.role,
          customer: response.customer,
          customerAccount: response.customerAccount,
        };
        
        // Store via AuthContext
        login(user, token);

        toast({
          title: 'Login Successful!',
          description: `Welcome back, ${user.firstName || user.userName || 'User'}! Redirecting...`,
        });

        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 1500);
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        error?.data?.data?.message ||
        'Login failed. Please check your credentials and try again.';
      
      toast({
        title: 'Login Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !signupData.firstName ||
      !signupData.lastName ||
      !signupData.company ||
      !signupData.userName ||
      !signupData.password ||
      !signupData.confirmPassword
    ) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    if (signupData.password.length < 6) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 6 characters long.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await saveCustomer({
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        company: signupData.company,
        userName: signupData.userName,
        password: signupData.password,
        roleId: signupData.roleId,
        customerAccount: signupData.customerAccount,
        nationalIdentity: signupData.nationalIdentity,
      });

      toast({
        title: 'Registration Successful!',
        description: 'Your account has been created. Please login to continue.',
      });

      // Switch to login form after successful registration
      setTimeout(() => {
        setIsLogin(true);
        setSignupData({
          firstName: '',
          lastName: '',
          company: '',
          userName: signupData.userName, // Keep username for convenience
          password: '',
          confirmPassword: '',
          roleId: 0,
          customerAccount: 0,
          nationalIdentity: '',
        });
      }, 2000);
    } catch (error) {
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        error?.response?.data?.message ||
        'Registration failed. Please try again.';
      
      toast({
        title: 'Registration Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">Customer Login</h1>
          <p className="text-lg text-gray-600 mb-8 text-center">
            {isLogin
              ? 'Sign in to your account to continue shopping'
              : 'Create a new account to get started'}
          </p>

          {/* Toggle Buttons */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg shadow p-1 inline-flex">
              <button
                onClick={() => setIsLogin(true)}
                className={`px-6 py-2 rounded transition font-semibold ${
                  isLogin
                    ? 'bg-red-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <LogIn size={18} />
                  <span>Login</span>
                </div>
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`px-6 py-2 rounded transition font-semibold ${
                  !isLogin
                    ? 'bg-red-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <UserPlus size={18} />
                  <span>Sign Up</span>
                </div>
              </button>
            </div>
          </div>

          {/* Login Form */}
          {isLogin && (
            <div className="bg-white rounded-lg shadow p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Sign In</h2>
              <form onSubmit={handleLogin}>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Username *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="userName"
                      value={loginData.userName}
                      onChange={handleLoginChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      placeholder="Enter your username"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="password"
                      name="password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 text-white py-3 rounded hover:bg-red-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </div>
          )}

          {/* Signup Form */}
          {!isLogin && (
            <div className="bg-white rounded-lg shadow p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Account</h2>
              <form onSubmit={handleSignup}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        name="firstName"
                        value={signupData.firstName}
                        onChange={handleSignupChange}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        placeholder="First name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        name="lastName"
                        value={signupData.lastName}
                        onChange={handleSignupChange}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="company"
                      value={signupData.company}
                      onChange={handleSignupChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      placeholder="Company name"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Username *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="userName"
                      value={signupData.userName}
                      onChange={handleSignupChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      placeholder="Choose a username"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="password"
                        name="password"
                        value={signupData.password}
                        onChange={handleSignupChange}
                        required
                        minLength={6}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        placeholder="Password (min 6 characters)"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="password"
                        name="confirmPassword"
                        value={signupData.confirmPassword}
                        onChange={handleSignupChange}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        placeholder="Confirm password"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Role ID
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="number"
                        name="roleId"
                        value={signupData.roleId}
                        onChange={handleSignupChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Customer Account
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="number"
                        name="customerAccount"
                        value={signupData.customerAccount}
                        onChange={handleSignupChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    National Identity
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="nationalIdentity"
                      value={signupData.nationalIdentity}
                      onChange={handleSignupChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      placeholder="National identity number (optional)"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 text-white py-3 rounded hover:bg-red-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;

